import { useEffect, useRef } from 'react'
import { ResponsiveImage } from 'components/Global/ResponsiveImage'
import { urlForImage } from 'lib/sanity.image'

export const ProductGallery = ({ images }) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!scrollRef.current) return

    const item = scrollRef.current.querySelector('[data-first-item]')
    const width = item?.clientWidth ?? 0
    let scrollTimer = -1

    const unsetBehavior = () => {
      if (!scrollRef.current) return
      scrollRef.current.style.scrollBehavior = 'unset'
    }

    const resetBehavior = () => {
      setTimeout(() => {
        if (!scrollRef.current) return
        scrollRef.current.style.scrollBehavior = 'smooth'
      }, 1)
    }

    unsetBehavior()
    scrollRef.current.scrollLeft = width
    resetBehavior()

    const handleScroll = () => {
      if (!scrollRef.current || !item) return
      const scroll = scrollRef.current.scrollLeft

      if (scrollTimer !== -1) {
        clearTimeout(scrollTimer)
      }

      const index = Math.round(scroll / width)

      const handleScrollEnd = () => {
        if (!scrollRef.current) return

        if (index < 1) {
          unsetBehavior()
          scrollRef.current.scrollLeft = width * images.length
          resetBehavior()
        }

        if (index > images.length) {
          unsetBehavior()
          scrollRef.current.scrollLeft = width
          resetBehavior()
        }
      }

      scrollTimer = window.setTimeout(handleScrollEnd, 50)
    }

    scrollRef.current.addEventListener('scroll', handleScroll)

    return () => {
      if (!scrollRef.current) return
      scrollRef.current.removeEventListener('scroll', handleScroll)
    }
  }, [])

  if (!images && images.length === 0) return null

  return (
    <div
      ref={scrollRef}
      className="scrollbar-hidden w-full snap-x snap-mandatory overflow-y-hidden overflow-x-scroll whitespace-nowrap md:grid md:flex-1 md:grid-cols-2 md:overflow-x-hidden"
    >
      {/* First Clone */}
      <div
        className="relative inline-block aspect-[2/3] h-auto w-full snap-start md:hidden"
        data-first-item
      >
        <ResponsiveImage
          src={
            images[images.length - 1]?.src
              ? images[images.length - 1].src
              : images[images.length - 1]?.asset
              ? urlForImage(images[images.length - 1])
                  .auto('format')
                  .width(1200)
                  .url()
              : ''
          }
          fill
          alt=""
          sizes="(min-width: 768px) 50vw, 100vw"
          priority
        />
      </div>
      {images?.map((image) => {
        return (
          <div
            key={image.id}
            className="relative inline-block aspect-[2/3] h-auto w-full snap-start md:h-auto"
          >
            <ResponsiveImage
              src={
                image.src
                  ? image.src
                  : image?.asset
                  ? urlForImage(image).auto('format').width(1200).url()
                  : ''
              }
              fill
              alt=""
              sizes="(min-width: 768px) 50vw, 100vw"
              priority
            />
          </div>
        )
      })}
      {/* Last Clone */}
      <div className="relative inline-block aspect-[2/3] h-auto w-full snap-start md:hidden">
        <ResponsiveImage
          src={
            images[0]?.src
              ? images[0].src
              : images[0]?.asset
              ? urlForImage(images[0]).auto('format').width(1200).url()
              : ''
          }
          fill
          alt=""
          sizes="(min-width: 768px) 50vw, 100vw"
          priority
        />
      </div>
    </div>
  )
}
